import React, { useState, useEffect } from 'react';
import CalendarView from './CalendarView';
import CardsView from './CardsView';
import ListView from './ListView';
import CalendarNavigation from './CalendarNavigation';
import InfoPopup from './InfoPopup';
import MapView from "./MapView";

interface Event {
	id: number;
	title: string;
	date: string;
	description: string;
}

interface EventsProps {
	showCalendar: boolean;
	showCards: boolean;
	showList: boolean;
	showMap: boolean;
	showSearch: boolean;
	eventsPerPage: number;
	events: Event[];
}

function Events({ showCalendar, showCards, showList, showMap, showSearch, eventsPerPage, events }: EventsProps) {
	const [view, setView] = useState<'calendar' | 'cards' | 'list' | 'map'>('calendar');
	const [value, onChange] = useState<Date>(new Date());
	const [activeStartDate, setActiveStartDate] = useState<Date>(new Date());
	const [fetchedEvents, setFetchedEvents] = useState<Event[]>(events);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
	const [locale, setLocale] = useState<string>('en-EN');

	// State for the active event and popup visibility
	const [activeEvent, setActiveEvent] = useState<Event | null>(null);
	const [showPopup, setShowPopup] = useState<boolean>(false);

	const filteredEvents = searchQuery.length >= 3
		? fetchedEvents.filter(event =>
			event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
			event.description.toLowerCase().includes(searchQuery.toLowerCase())
		)
		: fetchedEvents;

	const updateDate = (year: number, month: number) => {
		setActiveStartDate(new Date(year, month - 1, 1));
	};

	const goToNextMonth = () => {
		setActiveStartDate((prevDate) => {
			const newDate = new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1);
			setSelectedYear(newDate.getFullYear());
			return newDate;
		});
	};

	const goToPreviousMonth = () => {
		setActiveStartDate((prevDate) => {
			const newDate = new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1);
			setSelectedYear(newDate.getFullYear());
			return newDate;
		});
	};

	const goToCurrentMonth = () => {
		const currentDate = new Date();
		setActiveStartDate(currentDate);
		setSelectedYear(currentDate.getFullYear());
	};

	const handleMonthClick = (month: number) => {
		updateDate(selectedYear, month);
	};

	const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const newYear = parseInt(event.target.value, 10);
		const newDate = new Date(activeStartDate);
		newDate.setFullYear(newYear);
		setActiveStartDate(newDate);
	};

	const formatMonthYear = (date: Date) => {
		const options = { year: 'numeric', month: 'long' };
		return new Intl.DateTimeFormat(locale, options).format(date);
	};

	useEffect(() => {
		const fetchEvents = async () => {
			setIsLoading(true);
			try {
				const year = activeStartDate.getFullYear();
				const month = String(activeStartDate.getMonth() + 1).padStart(2, '0');
				const response = await fetch(`/wp-json/custom/v1/events?month=${month}&year=${year}`);
				if (!response.ok) {
					throw new Error('Network response was not ok');
				}
				const eventsData = await response.json();
				setFetchedEvents(eventsData);
			} catch (error) {
				console.error('Error fetching events:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchEvents();
	}, [activeStartDate]);

	useEffect(() => {
		setSelectedYear(activeStartDate.getFullYear());
	}, [activeStartDate]);

	const handleEventClick = (event: Event) => {
		setActiveEvent(event);
		setShowPopup(true);
	};

	const handleClosePopup = () => {
		setShowPopup(false);
	};

	return (
		<div>
			<CalendarNavigation
				activeStartDate={activeStartDate}
				locale={locale}
				selectedYear={selectedYear}
				goToPreviousMonth={goToPreviousMonth}
				goToCurrentMonth={goToCurrentMonth}
				goToNextMonth={goToNextMonth}
				handleMonthClick={handleMonthClick}
				handleYearChange={handleYearChange}
				formatMonthYear={formatMonthYear}
				view={view}
				setView={setView}
				showCalendar={showCalendar}
				showCards={showCards}
				showList={showList}
				showMap={showMap}
			/>
			<div className={'fsdpe-calendar-wrapper__views'}>
				{isLoading && (
					<div className="fsdpe-calendar-wrapper__views__loader">
					</div>
				)}
				{view === 'calendar' && showCalendar && (
					<CalendarView
						value={value}
						onChange={onChange}
						activeStartDate={activeStartDate}
						onActiveStartDateChange={({ activeStartDate }) => setActiveStartDate(activeStartDate)}
						events={filteredEvents}
						onEventClick={handleEventClick}
					/>
				)}
				{view === 'cards' && showCards &&
					<CardsView
						events={filteredEvents}
						eventsPerPage={eventsPerPage}
						onEventClick={handleEventClick}
					/>
				}
				{view === 'list' && showList &&
					<ListView
						events={filteredEvents}
						eventsPerPage={eventsPerPage}
						onEventClick={handleEventClick}  // Pass the click handler

					/>
				}
				{view === 'map' && showMap &&
					<MapView
						events={filteredEvents}
						onEventClick={handleEventClick}
					/>
				}
			</div>
			{showPopup && activeEvent && (
				<InfoPopup
					event={activeEvent}
					onClose={handleClosePopup}
					onEventClick={handleEventClick}  // Pass the click handler
				/>
			)}
		</div>
	);
}

export default Events;
